import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import ModalNotification from '@/shared/components/dialogs/ModalNotification';
import WaveUIAccordion from '@/shared/components/widgets/WaveUIAccordion';
import Customer from '@/shared/types/customer.type';

import AccountDetailsForm from './components/AccountDetailsForm';
import BillingAddressForm from './components/BillingAddressForm';
import ShippingAddressForm from './components/ShippingAddressForm';

type CreateCustomerModalProps = {
  open: boolean;
  customer?: Customer;
  onClose: () => void;
  proceed: (customer: Partial<Customer>, customerId?: string) => void;
};

const CreateCustomerModal: FC<CreateCustomerModalProps> = ({
  open,
  customer,
  onClose,
  proceed,
}) => {
  const { t } = useTranslation(['customers', 'common']);

  const [accountDetailsForm, setAccountDetailsForm] = useState({
    first_name: customer?.first_name || '',
    last_name: customer?.last_name || '',
    email_address: customer?.email_address || '',
    phone_number: customer?.phone_number || '',
  });

  const [shippingAddressForm, setShippingAddressForm] = useState({
    delivery_country: customer?.delivery_country || '',
    delivery_address: customer?.delivery_address || '',
    delivery_address2: customer?.delivery_address2 || '',
    delivery_city: customer?.delivery_city || '',
    delivery_state: customer?.delivery_state || '',
    delivery_postcode: customer?.delivery_postcode || '',
  });

  const [billingAddressForm, setBillingAddressForm] = useState({
    billing_country: customer?.billing_country || '',
    billing_address: customer?.billing_address || '',
    billing_address2: customer?.billing_address2 || '',
    billing_city: customer?.billing_city || '',
    billing_state: customer?.billing_state || '',
    billing_postcode: customer?.billing_postcode || '',
  });

  const [formsErrors, setFormsErrors] = useState<
    Record<string, Record<string, string>>
  >({
    accountDetails: {
      first_name: '',
      last_name: '',
      email_address: '',
      phone_number: '',
    },
  });

  const [isShippingSameAsDelivery, setIsShippingSameAsDelivery] =
    useState(false);

  useEffect(() => {
    console.log('test');
    if (isShippingSameAsDelivery) {
      setBillingAddressForm((prev) => ({
        ...prev,
        billing_country: shippingAddressForm.delivery_country,
        billing_address: shippingAddressForm.delivery_address,
        billing_address2: shippingAddressForm.delivery_address2,
        billing_city: shippingAddressForm.delivery_city,
        billing_state: shippingAddressForm.delivery_state,
        billing_postcode: shippingAddressForm.delivery_postcode,
      }));
    }
  }, [
    isShippingSameAsDelivery,
    shippingAddressForm.delivery_address,
    shippingAddressForm.delivery_address2,
    shippingAddressForm.delivery_city,
    shippingAddressForm.delivery_country,
    shippingAddressForm.delivery_postcode,
    shippingAddressForm.delivery_state,
  ]);

  return (
    <ModalNotification
      open={open}
      onClose={onClose}
      size='sm'
      dialogProps={{
        title: customer
          ? t('customers:editCustomer')
          : t('customers:createCustomer'),
        content: (
          <Stack spacing={2}>
            <WaveUIAccordion
              title={t('customers:accountDetails')}
              defaultExpanded
            >
              <AccountDetailsForm
                formValues={accountDetailsForm}
                errors={formsErrors.accountDetails}
                setFormValues={(values) => {
                  setAccountDetailsForm(
                    values as unknown as typeof accountDetailsForm,
                  );
                }}
                setFormErrors={(errors) => {
                  setFormsErrors({ ...formsErrors, accountDetails: errors });
                }}
              />
            </WaveUIAccordion>

            <WaveUIAccordion title={t('customers:shippingAddress')}>
              <Typography variant='caption' textTransform='uppercase'>
                {t('customers:shippingAddress')}
              </Typography>

              <ShippingAddressForm
                formValues={shippingAddressForm}
                setFormValues={(values) =>
                  setShippingAddressForm(
                    values as unknown as typeof shippingAddressForm,
                  )
                }
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={isShippingSameAsDelivery}
                    onChange={(_, checked) =>
                      setIsShippingSameAsDelivery(checked)
                    }
                  />
                }
                label='Set billing address the same as shipping'
                labelPlacement='end'
              />
            </WaveUIAccordion>

            {!isShippingSameAsDelivery && (
              <WaveUIAccordion title={t('customers:billingAddress')}>
                <Typography variant='caption' textTransform='uppercase'>
                  {t('customers:billingAddress')}
                </Typography>

                <BillingAddressForm
                  formValues={billingAddressForm}
                  setFormValues={(values) =>
                    setBillingAddressForm(
                      values as unknown as typeof billingAddressForm,
                    )
                  }
                />
              </WaveUIAccordion>
            )}
          </Stack>
        ),
        actions: [
          {
            label: t('common:cancel'),
            variant: 'outlined',
            onClick: onClose,
          },
          {
            label: customer ? t('common:edit') : t('common:create'),
            variant: 'contained',
            disabled:
              !!Object.values(formsErrors).some((errors) => {
                return !!Object.values(errors).some((error) => !!error);
              }) ||
              !accountDetailsForm.first_name ||
              !accountDetailsForm.last_name ||
              !accountDetailsForm.email_address ||
              !accountDetailsForm.phone_number,
            onClick: () =>
              proceed(
                {
                  ...accountDetailsForm,
                  ...shippingAddressForm,
                  ...billingAddressForm,
                },
                customer?.in_customer_id,
              ),
          },
        ],
      }}
    />
  );
};

export default CreateCustomerModal;
